import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DollarSign, CreditCard, TrendingUp, ShoppingCart, Megaphone, Calculator,
    LineChart, BarChart3, Percent, Scale, Table, Activity, GitCompare,
    Target, Crosshair, Gauge, PieChart, UserPlus, Trophy, CircleDot,
    LayoutGrid, Filter, Eye, MousePointerClick, Trash2, MousePointer,
    Crown, BarChart2, Package, ListOrdered, Tag, RotateCcw, AlertTriangle, LayoutList,
    Receipt, TrendingDown, Banknote, BadgePercent, Truck, PackageCheck,
    Tags, BookOpen, BookText, Coins, AlertOctagon,
    ArrowUpDown, Wallet, Clock, Calendar, Flag, GitBranch,
    Undo2, ArrowDown, Split, CircleDollarSign, PackageX,
    Shuffle, GitFork, Link, ExternalLink, HelpCircle,
    Repeat, Users, UserCircle, UserCheck, User,
    BarChart, BarChartHorizontal, Sparkle, Rocket, Zap, CalendarDays,
    Sparkles, FlaskConical, Lightbulb, Pin, Snowflake, Sliders,
    History, MapPin, ArrowUp, Star, ToggleLeft, PlayCircle, RefreshCw, UserCog, CheckCircle, Timer,
    Inbox, Ban, Gift, AlertCircle,
    ShoppingBag, XCircle, Layers, FileWarning, Hash, ArrowLeftRight,
    type LucideIcon,
} from 'lucide-react';
import { getAvailableChartDefinitions, ChartCategory, SizeTier, ChartDefinition } from './charts';

interface ChartCatalogProps {
    onDragStart: (type: string, sizeTier: SizeTier, aspectRatio: string) => void;
    onDragEnd?: () => void;
    onClick: (type: string) => void;
    selectedCategory?: ChartCategory | 'All';
    filteredCharts?: ChartDefinition[];
}

// Unique icon for every chart type
const CHART_ICON_MAP: Record<string, LucideIcon> = {
    // Basic KPIs
    'total_revenue': DollarSign,
    'total_expenses': CreditCard,
    'net_profit_kpi': TrendingUp,
    'orders_kpi': ShoppingCart,
    'ad_spend_kpi': Megaphone,
    'aov_kpi': Calculator,

    // Profitability
    'net_profit_over_time': LineChart,
    'profit_waterfall': BarChart3,
    'profit_margin_over_time': Percent,
    'income_vs_costs': Scale,
    'daily_pl_table': Table,
    'running_profit': Activity,
    'profit_period_comparison': GitCompare,

    // Ad Performance
    'roas_by_platform': Target,
    'roas_over_time': Crosshair,
    'blended_mer': Gauge,
    'ad_spend_percent': PieChart,
    'cpa_over_time': UserPlus,
    'campaign_roas_leaderboard': Trophy,
    'ad_set_efficiency_scatter': CircleDot,
    'creative_performance_table': LayoutGrid,
    'ad_funnel': Filter,
    'cpm_trend': Eye,
    'cpc_trend': MousePointerClick,
    'wasted_ad_spend': Trash2,
    'ctr_over_time': MousePointer,

    // Product Performance
    'best_sellers_by_revenue': Crown,
    'revenue_concentration': BarChart2,
    'product_sales_trend': Package,
    'units_sold_ranking': ListOrdered,
    'revenue_per_unit': Tag,
    'product_return_rate': RotateCcw,
    'refund_damage_by_product': AlertTriangle,
    'product_channel_matrix': LayoutList,

    // Cost Breakdown
    'expense_category_donut': Receipt,
    'expense_category_trend': TrendingDown,
    'payment_processing_fees': Banknote,
    'payment_fees_percent': BadgePercent,
    'shipping_cost_over_time': Truck,
    'shipping_per_order': PackageCheck,
    'discount_total_and_rate': Tags,
    'journal_expense_breakdown': BookOpen,
    'journal_expenses_over_time': BookText,
    'true_cost_per_order': Coins,
    'expense_anomaly_detector': AlertOctagon,

    // Cash Flow
    'daily_inflows_outflows': ArrowUpDown,
    'cumulative_cash_position': Wallet,
    'ad_spend_vs_revenue_timing': Clock,
    'weekly_cash_summary': Calendar,
    'break_even_day': Flag,
    'money_flow_sankey': GitBranch,

    // Refunds
    'return_rate_over_time': Undo2,
    'refund_amount_over_time': ArrowDown,
    'refund_type_breakdown': Split,
    'refund_true_cost': CircleDollarSign,
    'most_returned_products': PackageX,

    // Traffic & Attribution
    'paid_vs_organic_revenue': Shuffle,
    'paid_vs_organic_trend': GitFork,
    'revenue_by_utm_source': Link,
    'revenue_by_utm_campaign': ExternalLink,
    'unattributed_revenue': HelpCircle,

    // Customer Insights
    'purchase_frequency': Repeat,
    'top_customers_by_revenue': Users,
    'customer_concentration': UserCircle,
    'new_vs_returning_revenue': UserCheck,
    'avg_revenue_per_customer': User,

    // Growth & Scaling
    'revenue_period_comparison': BarChart,
    'order_volume_over_time': BarChartHorizontal,
    'aov_trend': Sparkle,
    'revenue_vs_ad_spend_growth': Rocket,
    'spend_vs_roas_scatter': Zap,
    'sales_by_day_of_week': CalendarDays,

    // Forecasting
    'revenue_forecast': Sparkles,
    'expense_forecast': FlaskConical,
    'profit_forecast': Lightbulb,
    'break_even_projection': Pin,
    'seasonal_index': Snowflake,
    'what_if_simulator': Sliders,

    // Campaign & Ad Changes
    'activity_timeline': History,
    'revenue_with_action_markers': MapPin,
    'budget_change_impact': ArrowUp,
    'budget_increase_efficiency': Star,
    'campaign_toggle_impact': ToggleLeft,
    'pause_resume_comparison': PlayCircle,
    'creative_swap_performance': RefreshCw,
    'audience_change_impact': UserCog,
    'ad_approval_tracker': CheckCircle,
    'campaign_lifecycle': Timer,

    // Store & Product Changes
    'product_launch_performance': Inbox,
    'product_removal_impact': Ban,
    'promo_discount_rule_impact': Gift,
    'refund_spike_analysis': AlertCircle,

    // Operational Health
    'order_fulfillment_funnel': ShoppingBag,
    'cancellation_rate_over_time': XCircle,
    'activity_volume_vs_revenue': Layers,
    'billing_issues_timeline': FileWarning,
    'action_breakdown_by_type': Hash,
    'before_after_any_action': ArrowLeftRight,
};

const getChartIcon = (chartType: string, className: string = "w-3.5 h-3.5") => {
    const IconComponent = CHART_ICON_MAP[chartType] || DollarSign;
    return <IconComponent className={className} />;
};

export const ChartCatalog: React.FC<ChartCatalogProps> = ({
    onDragStart,
    onDragEnd,
    onClick,
    selectedCategory = 'All',
    filteredCharts: propsFilteredCharts
}) => {
    const { t } = useTranslation('common');

    const filteredCharts = useMemo(() => {
        if (propsFilteredCharts) return propsFilteredCharts;
        const available = getAvailableChartDefinitions();
        return selectedCategory === 'All'
            ? available
            : available.filter(chart => chart.categories.includes(selectedCategory as ChartCategory));
    }, [selectedCategory, propsFilteredCharts]);


    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
            {filteredCharts.map((chart, idx) => {
                // Generate a color based on index
                const getPreviewColor = (i: number) => {
                    const colors = [
                        "bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-900/30 text-blue-500 dark:text-blue-400",
                        "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-900/30 text-emerald-500 dark:text-emerald-400",
                        "bg-violet-50 dark:bg-violet-900/20 border-violet-100 dark:border-violet-900/30 text-violet-500 dark:text-violet-400",
                        "bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-900/30 text-amber-500 dark:text-amber-400",
                        "bg-rose-50 dark:bg-rose-900/20 border-rose-100 dark:border-rose-900/30 text-rose-500 dark:text-rose-400",
                        "bg-indigo-50 dark:bg-indigo-900/20 border-indigo-100 dark:border-indigo-900/30 text-indigo-500 dark:text-indigo-400"
                    ];
                    return colors[i % colors.length];
                };

                const colorClass = getPreviewColor(idx);

                return (
                    <div
                        key={chart.type}
                        className="group relative flex flex-col bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl cursor-pointer hover:border-accent-primary dark:hover:border-accent-primary hover:shadow-xl hover:shadow-accent-primary/5 transition-all duration-300 overflow-hidden h-44"
                        draggable={true}
                        unselectable="on"
                        // this is a hack for firefox
                        // @ts-ignore
                        onDragStart={(e) => {
                            e.dataTransfer.setData("text/plain", chart.type);
                            e.dataTransfer.effectAllowed = "copy";
                            onDragStart(chart.type, chart.defaultSizeTier, chart.defaultAspectRatio);
                        }}
                        onDragEnd={() => {
                            onDragEnd?.();
                        }}
                        onClick={() => {
                            onClick(chart.type);
                        }}
                    >
                        {/* Preview Area */}
                        <div className={`flex-1 flex items-center justify-center relative overflow-hidden border-b dark:border-neutral-800 transition-colors duration-300 ${colorClass} group-hover:bg-opacity-80`}>
                            <div className="transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                                {getChartIcon(chart.type, "w-14 h-14")}
                            </div>

                            {/* Action indicators */}
                            <div className="absolute top-3 right-3 flex gap-1 transition-all duration-300">
                                <div className="p-1.5 bg-white dark:bg-neutral-800 backdrop-blur-md rounded-lg shadow-lg text-accent-primary" title={t("charts.dragToAdd") || "Drag to add"}>
                                    <span className="w-4 h-4" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_d9ac5c3e.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_d9ac5c3e.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-4 bg-white dark:bg-neutral-900">
                            <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-200 group-hover:text-accent-primary dark:group-hover:text-accent-primary transition-colors duration-200 truncate">
                                {t(`charts.titles.${chart.type}`) || chart.title}
                            </h3>
                            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                                {t(`charts.descriptions.${chart.type}`) || chart.description}
                            </p>
                        </div>
                    </div>
                );
            })}

            {filteredCharts.length === 0 && (
                <div className="col-span-full py-20 flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-800 rounded-2xl flex items-center justify-center mb-6 text-neutral-400 dark:text-neutral-500">
                        <span className="w-8 h-8" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_d820a918.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_d820a918.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                    </div>
                    <h3 className="text-base font-semibold text-neutral-700 dark:text-neutral-200 mb-2">
                        {t("charts.empty.title") || "No charts in this category"}
                    </h3>
                    <p className="text-sm text-neutral-400 dark:text-neutral-500 max-w-xs">
                        {t("charts.empty.subtitle") || "We're working on adding more charts soon!"}
                    </p>
                </div>
            )}
        </div>
    );
};
